// ── VolumeControl — громкость как в YouTube / Telegram ───────────────────────
// Значок динамика (нажатие — выключить / включить звук) и плавный ползунок:
// тянется пальцем или мышью без шагов, колёсико мыши над регулятором меняет
// громкость на 5 % (страница при этом не прокручивается), стрелки — тоже 5 %.
// compact: ползунок раскрывается при наведении или фокусе, как в YouTube.
//
// onInput(v) — на каждое движение (сразу ставим громкость звуку, без перерисовки
// плеера), onChange(v) — когда значение «установилось» (отпустили, шаг колёсика,
// клавиша): тут сохраняем.

import { useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import { t } from './i18n';

const clamp01 = (v) => Math.min(1, Math.max(0, v));

export default function VolumeControl({ C, value = 1, muted = false, onInput, onChange, onMute, width = 96, compact = false, showPct = false }) {
  const [live, setLive] = useState(null); // значение, пока тянем
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const shown = muted ? 0 : (live ?? value);
  const boxRef = useRef(null);
  const trackRef = useRef(null);
  const cur = useRef(shown);
  cur.current = shown;
  const cbs = useRef({ onInput, onChange, onMute, muted });
  cbs.current = { onInput, onChange, onMute, muted };

  const set = (v, commit) => {
    v = Math.round(clamp01(v) * 100) / 100;
    if (cbs.current.muted && v > 0) cbs.current.onMute?.(); // тронули громкость — звук снова включён
    cbs.current.onInput?.(v);
    if (commit) { setLive(null); cbs.current.onChange?.(v); } else setLive(v);
  };
  const fromX = (x) => { const r = trackRef.current?.getBoundingClientRect(); return r && r.width ? (x - r.left) / r.width : cur.current; };

  // Колёсико: обработчик не пассивный, иначе страница прокрутится вместе с громкостью.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const step = e.deltaY < 0 ? 0.05 : e.deltaY > 0 ? -0.05 : 0;
      if (step) set(cur.current + step, true);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fill = width === 'fill';
  const dragging = live != null;
  const open = !compact || hover || focus || dragging;
  const thick = hover || dragging || focus;
  const pct = Math.round(shown * 100);
  const icon = shown === 0 ? 'mute' : shown < 0.4 ? 'volumeLow' : 'volume';

  return (
    <div ref={boxRef} onPointerEnter={() => setHover(true)} onPointerLeave={() => setHover(false)}
      title={t("Громкость {0}% — колёсико мыши меняет", [pct])}
      style={{ display: fill ? 'flex' : 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, ...(fill ? { flex: 1, minWidth: 0 } : {}) }}>
      <button onClick={() => cbs.current.onMute?.()} aria-label={muted || shown === 0 ? t("Включить звук") : t("Выключить звук")} className="ac-ibtn"
        style={{ width: 40, height: 40, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', borderRadius: 8, color: shown === 0 ? C.faint : C.mid, cursor: 'pointer', padding: 0 }}>
        <Icon n={icon} s={18} />
      </button>
      {/* Свёрнутый ползунок не только прозрачный, но и неосязаемый: раньше невидимая полоса
          шириной 96 px лежала поверх соседних кнопок (очередь, «открыть плеер») и ловила нажатия. */}
      <div style={fill ? { flex: 1, minWidth: 0, padding: '0 7px' } : { width: open ? width : 0, opacity: open ? 1 : 0, overflow: open ? 'visible' : 'hidden',
        visibility: open ? 'visible' : 'hidden', pointerEvents: open ? 'auto' : 'none', transition: 'width .18s ease, opacity .18s ease, visibility .18s', flexShrink: 0 }}>
        <div ref={trackRef} role="slider" tabIndex={open ? 0 : -1} aria-label={t("Громкость")} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-valuetext={`${pct}%`}
          onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
          onPointerDown={(e) => { e.preventDefault(); try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* старый браузер */ } set(fromX(e.clientX), false); }}
          onPointerMove={(e) => { if (live != null) set(fromX(e.clientX), false); }}
          onPointerUp={(e) => { if (live != null) set(fromX(e.clientX), true); }}
          onPointerCancel={() => { if (live != null) set(cur.current, true); }}
          onKeyDown={(e) => {
            const k = e.key;
            if (k === 'ArrowRight' || k === 'ArrowUp') { e.preventDefault(); set(cur.current + 0.05, true); }
            else if (k === 'ArrowLeft' || k === 'ArrowDown') { e.preventDefault(); set(cur.current - 0.05, true); }
            else if (k === 'Home') { e.preventDefault(); set(0, true); }
            else if (k === 'End') { e.preventDefault(); set(1, true); }
          }}
          style={{ position: 'relative', height: 32, display: 'flex', alignItems: 'center', cursor: 'pointer', touchAction: 'none', outline: 'none', width: fill ? '100%' : width }}>
          <div style={{ position: 'absolute', left: 0, right: 0, height: thick ? 6 : 4, borderRadius: 3, background: C.border, transition: 'height .12s' }}>
            <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: C.blue }} />
          </div>
          <div style={{ position: 'absolute', left: `${pct}%`, top: '50%', width: 14, height: 14, borderRadius: '50%', background: C.blue,
            transform: `translate(-50%,-50%) scale(${thick ? 1 : 0.85})`, transition: 'transform .12s', boxShadow: focus ? `0 0 0 3px ${C.bluePale}` : 'none', pointerEvents: 'none' }} />
        </div>
      </div>
      {showPct && <span style={{ fontSize: 12.5, color: C.mid, minWidth: 36, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>}
    </div>
  );
}
